import type { MailboxInfo } from 'owa-client-types';
import { getMailboxInfo } from '../../selectors/mailRowDataPropertyGetter';
import { isUnifiedInboxMailboxInfo } from 'owa-client-types/lib/UnifiedInboxMailboxInfo';
import type TableView from '../../store/schema/TableView';
import { isSameMailboxInfo } from 'owa-account-source-list-store';
import mailStore from 'owa-mail-store/lib/store/Store';

function getMailboxInfoFromRowKeyOrId(
    rowKeyOrId: string,
    tableView?: TableView
): MailboxInfo | undefined {
    if (tableView) {
        return (
            getMailboxInfo(rowKeyOrId, tableView) ?? mailStore().items.get(rowKeyOrId)?.MailboxInfo
        );
    } else {
        return mailStore().items.get(rowKeyOrId)?.MailboxInfo;
    }
}

// Returns the mailbox info if all row keys or item ids are from the same mailbox, otherwise returns the default mailbox info
// This is used to ensure that we are not sending multiple mailbox info in the same request
// We will eventually fan out the requests to multiple mailboxes so this will become a Map<mailboxInfo, itemIds[]>
export function tryGetMailboxInfoFromRowKeysOrIdsIfUnifiedInboxOrDefault(
    defaultMailboxInfo: MailboxInfo,
    rowKeysOrIds: string[],
    tableView?: TableView
): MailboxInfo {
    if (isUnifiedInboxMailboxInfo(defaultMailboxInfo)) {
        const mailboxInfos = rowKeysOrIds
            .map(rowKeyOrId => getMailboxInfoFromRowKeyOrId(rowKeyOrId, tableView))
            .filter(Boolean) as MailboxInfo[];

        // If no mailbox info could be resolved for any items, throw
        if (mailboxInfos.length === 0) {
            throw new Error(
                'For UnifiedInbox, unable to resolve mailbox info for any of the provided row keys or item IDs.'
            );
        }

        // Check if all mailboxInfos are the same
        // Go through the mailboxInfos array and compare previous and current mailboxInfo
        for (let i = 1; i < mailboxInfos.length; i++) {
            if (!isSameMailboxInfo(mailboxInfos[i - 1], mailboxInfos[i])) {
                throw new Error(
                    "For UnifiedInbox, we can't process row keys from different mailboxes in the same request."
                );
            }
        }
        // If all mailboxInfos are the same, return the first one
        return mailboxInfos[0];
    }

    return defaultMailboxInfo;
}
